/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useState } from 'react';

import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap, jsx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { OnlineDebuggerButton } from '../../../online-debugger/online-debugger-button';
import { messages } from './messages';
import { ViewAllButton } from './ViewAllButton';

const styles = cssMap({
	container: {
		borderRadius: token('radius.large'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('elevation.surface.overlay'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	messageContainer: {
		flex: 1,
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
	},
	viewAllContainer: {
		textAlign: 'left',
	},
});

export type JiraNlToJqlErrorCardProps = {
	query: string;
	viewAllUrl: string;
	onViewAllClick?: () => void;
};

export function JiraNlToJqlErrorCard({
	query,
	viewAllUrl,
	onViewAllClick,
}: JiraNlToJqlErrorCardProps): JSX.Element {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(true);

	if (!isOpen) {
		return <Fragment />;
	}

	return (
		<Stack space="space.200" xcss={styles.container} testId="jira-nl-to-jql-error-card">
			<Inline spread="space-between" alignBlock="start" space="space.100">
				<Box xcss={styles.messageContainer}>
					<Text>{formatMessage(messages.errorMessageWithViewAll, { query })}</Text>
				</Box>
				<IconButton
					appearance="subtle"
					icon={CrossIcon}
					label={formatMessage(messages.close)}
					onClick={() => setIsOpen(false)}
					testId="jira-nl-to-jql-error-card-close"
				/>
			</Inline>
			<Inline spread="space-between" alignBlock="center" space="space.100">
				<Box xcss={styles.viewAllContainer}>
					<ViewAllButton
						url={viewAllUrl}
						callback={onViewAllClick ?? (() => {})}
						spacing="compact"
					/>
				</Box>
				{fg('kd_sain_online_debugger') && <OnlineDebuggerButton />}
			</Inline>
		</Stack>
	);
}
